// The v1 scope vocabulary — the strings that name a capability, and nothing else.
//
// It lives in `@developerz/domain` because TWO packages gate on it and neither
// may import the other. It was declared in `apps/api/src/middleware/auth.ts`,
// which `packages/mcp` structurally cannot reach (`docs/idea/monorepo.md`: apps
// never import apps, and packages never import apps), so every MCP verb restated
// its scope as a bare string literal — 42 of them across `army/*.ts` and
// `tools.ts` on 2026-09-04. A literal cannot be typo-checked against a
// vocabulary it has no reference to.
//
// WHAT IS HERE AND WHAT IS NOT. This module is the VOCABULARY — the set of legal
// strings — plus ONE piece of policy: the role -> scope CEILING
// (`ROLE_SCOPE_CEILING` / `scopeCeilingForRole`, bottom of this file). The
// ceiling moved here (plan 101 A3) because two seams must apply the same table
// and neither may import the other: the API mints and caps sessions with it
// (`apps/api/src/middleware/scopes.ts`), and `packages/db` `resolveApiToken`
// intersects a PAT's stored scopes with it, so a DEMOTED user's token loses
// what the new role no longer carries. Everything else about who holds what —
// `ADMIN_READ_SCOPES`, `ADMIN_WRITE_SCOPES`, `sessionScopes`, the staff grant —
// stays in `apps/api/src/middleware/scopes.ts`, where a session is in scope.
//
// KNOWN DIVERGENCE, and it is not closed by this move. `packages/mcp`'s
// `army/box.ts` gates `attest_isolation` / `withdraw_isolation` on
// `'admin:runner:isolation'`, which is NOT a member below and never has been
// (#2889): no credential can carry a string this vocabulary does not contain, so
// both actions are unreachable in production. That is tracked and pinned red by
// `apps/api/src/capability-reachability.test.ts` (`KNOWN_UNREACHABLE`), and
// adding the member here would be a security change — `POST /v1/tokens` mints
// from `z.enum(Object.values(Scope))` — not a rename. It is listed here so the
// next reader of this file finds the gap rather than the silence.

import { Role } from './entities/account';

/** Scopes recognized by v1. Routes assert `requireScope(scope)`. */
export const Scope = {
  ReadAccount: 'read:account',
  WriteAccount: 'write:account',
  /** Manage org members: invite / change-role / remove (#308). Owner + Admin. */
  WriteMembers: 'write:members',
  ReadRepos: 'read:repos',
  WriteRepos: 'write:repos',
  ReadIssues: 'read:issues',
  WriteIssues: 'write:issues',
  ReadPrs: 'read:prs',
  WritePrs: 'write:prs',
  ReadSessions: 'read:sessions',
  WriteSessions: 'write:sessions',
  ReadWebhooks: 'read:webhooks',
  WriteWebhooks: 'write:webhooks',
  ReadDigests: 'read:digests',
  ReadUsage: 'read:usage',
  ReadBilling: 'read:billing',
  WriteBilling: 'write:billing',
  /**
   * A member's OWN provider keys + model default (the per-user provider cascade,
   * epic #635). Member-level: distinct from `write:billing` (owner/admin — the
   * ORG pool). A member may bring their own BYOK key / set a model default for
   * their own dispatched work without any authority over the org's shared keys.
   */
  WriteSelfKeys: 'write:self:keys',
  /**
   * A member's OWN system-prompt layer (epic #635 cascade, slice 2) — the prose
   * that specializes their own dispatched work. Member-level, scoped to
   * `(account, self)`; distinct from `write:prompts` (owner/admin — the org + repo
   * layers).
   */
  WriteSelfPrompt: 'write:self:prompt',
  /**
   * A person's OWN notification settings on one account: which emails and
   * browser notifications they get, and how often. Self-service, scoped to
   * `(account, self)`, and held by EVERY role, viewer included: a viewer is a
   * fan-out recipient, and without this their only way to stop a mail is the
   * spam button. Distinct from `write:account` (owner only), which still gates
   * the account's email routes. It has no `read:` twin on purpose: the read
   * rides `read:account`, so a read-only third-party token can see the settings
   * and can never silence someone's mail.
   */
  WriteSelfNotifications: 'write:self:notifications',
  /**
   * A person's OWN sign-in identities (#466, #1738): unlink an SSO login from
   * themselves (`DELETE /v1/me/identities/:id`). Held by EVERY role, viewer
   * included — a login belongs to the human, not to whichever org the session is
   * acting as — and never by a CAPPED session (waitlisted, ToS or consent owed:
   * the cap is `read:account` alone), the line #1738 drew after finding 2 let a
   * read scope rewrite an identity. LINKING has no `/v1` door at all: it is the
   * IdP round-trip `/auth/org/:slug/link`, bound to a live session. The list
   * rides `read:account`, so a read-only token can see the logins and never
   * remove one.
   */
  WriteSelfIdentity: 'write:self:identity',
  /**
   * The org + repo system-prompt layers (epic #635 cascade, slice 2). Owner/admin:
   * account-wide + per-repo maintainer prose that specializes the platform floor.
   * The narrower per-member layer is `write:self:prompt` (member-level).
   */
  WritePrompts: 'write:prompts',
  ReadTasks: 'read:tasks',
  WriteTasks: 'write:tasks',
  ReadRunners: 'read:runners',
  WriteRunners: 'write:runners',
  /**
   * Hold a repository's merges for review, or clear the hold (#7011): the platform-side
   * merge override `PUT /v1/repos/:owner/:repo/merge-override` and MCP
   * `project set_merge_override`. Owner ruling (ivndev001, 2026-10-21, grill): a narrow
   * ADMIN-level scope, so an admin can use the Fleet page's Merge Gate without the
   * owner-only `write:repos`. Spelled with a colon, not the ruling's example underscore:
   * the vocabulary admits only colon-joined lowercase words (`scopes.test.ts`).
   */
  WriteMergeGate: 'write:merge:gate',
  /**
   * Create, rename, re-member and delete SQUADS, admin-named groups of the account's
   * repos (#7010): `POST|PATCH|DELETE /v1/squads` and MCP `squad create` / `rename` /
   * `set_repos` / `delete`. Owner ruling (ivndev001, 2026-10-21, grill): a narrow
   * ADMIN-level scope beside {@link Scope.WriteMergeGate}, so an admin can group repos
   * without the owner-only `write:repos`. The read rides `read:repos`.
   */
  WriteSquads: 'write:squads',
  // ── control-plane feature vocabulary (arc 101 slice 03, PR7) ────────────────
  // The REST↔MCP verb-parity capability set. Defined here NOW so the token
  // mint-gate (`z.enum(Object.values(Scope))` in `routes/tokens.ts`) and the
  // role ceilings below stay coherent, and each feature's landing route/verb is
  // a plain `requireScope(...)`. The *mirroring* REST route + MCP verb ship in
  // that feature's own slice (cited per scope) under "no abstractions before
  // consumers" — this PR reserves the vocabulary + role placement, not phantom
  // routes. See `docs/plans/2026/07/09/101-platform-vision-arc/03-control-plane-mcp-api.md`.
  /**
   * Knowledge-base pages (repo/account memory `kind:page`). **Live** — the MCP
   * verbs `kb_search` / `kb_page_get` (`read:kb`) and `kb_page_upsert`
   * (`write:kb`) gate on these (`packages/mcp/src/tools.ts` `FLAT_TOOL_SCOPES`).
   * REST mirrors it since the `/v1/kb` surface landed: `kb.page_list`,
   * `kb.search` and `kb.page_get` gate on `read:kb`, `kb.page_upsert` on
   * `write:kb` (`apps/api/src/routes/kb.ts`). This line said "MCP-only entity:
   * no REST route mirrors it" for as long as those four routes existed — a
   * comment asserting a guarantee the code no longer provided. The KB is its own
   * capability — `read|write:repos` does NOT imply it, so a repo-admin token
   * carries no authority over the wiki the agent reasons from. Repo READ is the
   * tenancy gate underneath: each verb resolves `owner/repo` to an enabled repo
   * of the caller's account before touching a page.
   */
  ReadKb: 'read:kb',
  WriteKb: 'write:kb',
  /** Artifact store (BYO-S3 outputs, presigned share). Route+verb: slice 08 (tasks 75–77). */
  ReadArtifacts: 'read:artifacts',
  WriteArtifacts: 'write:artifacts',
  /** Integrations catalog (Sentry/Slack/S3/…). Route+verb: slice 03 PR8 (task 30). */
  ReadIntegrations: 'read:integrations',
  WriteIntegrations: 'write:integrations',
  /** OSS donations (earmarks, backer stats). Route+verb: slice 10 (tasks 87–88). */
  ReadDonations: 'read:donations',
  WriteDonations: 'write:donations',
  /**
   * Credential-vault secrets. A read NEVER yields plaintext — it lists secret
   * names / class / exposure flag only (sealed-by-default model; values are
   * injected server-side or materialized on-box, never returned by the API).
   * That invariant is the landing route's job to honour. Route+verb: slice 03
   * PR8 (task 31, two-tier exposure model).
   *
   * **Both faces are OWNER-ONLY** (#1418) — see the ceiling note below. The read
   * is not a `read:*` a viewer holds: on a shared account, learning that a repo
   * carries a `STRIPE_LIVE_KEY` in `prod` is itself the disclosure.
   */
  ReadSecrets: 'read:secrets',
  WriteSecrets: 'write:secrets',
  /** Realtime dashboards (JSON-defined widgets). Route+verb: slice 09 (task 80). */
  ReadDashboards: 'read:dashboards',
  WriteDashboards: 'write:dashboards',
  // ── platform-admin READ vocabulary (admin.developerz.ai — the platform radar) ──
  // Deliberately `admin:` prefixed, NOT `read:` — the ONLY holder is a
  // developerz-ai staff session (the `platformAdmin` grant below) or an operator
  // PAT. The `admin:` prefix keeps them OUT of every role ceiling (each derived
  // from `read:*` / a `!admin:` filter), so no account owner, member, or session
  // ever silently gains a cross-account platform view. Writes (approve/suspend user, edit org
  // tier/seats/spend-cap) are NOT here — they stay operator-token-only through the
  // MCP `user` / `account` verbs (`admin:users` / `admin:accounts`, kept out of
  // this enum entirely, per `packages/mcp/CLAUDE.md`). The ONE exception is
  // `admin:grants` below — entitlement grants are served over REST as well as
  // MCP, and a REST route asserts its scope through `requireScope(scope: Scope)`.
  /** Platform overview + global audit tail (accounts/runs/fleet/queue rollups). */
  ReadAdminPlatform: 'admin:read:platform',
  /** Org (account) management reads: list/detail with tier/seats/spend-cap/members. */
  ReadAdminOrgs: 'admin:read:orgs',
  /** User management reads: registry with approval state + memberships. */
  ReadAdminUsers: 'admin:read:users',
  /**
   * CROSS-TENANT RUN READS: one BYOVM run's pulse and its durable event
   * transcript, whichever account owns it (`GET /v1/admin/fleet/runs/:runId*`).
   * The staff read behind "we can and will look at your runs to improve the
   * platform" — a disclosure that was true of nobody's tooling: there was no
   * cross-org run read at any tier, so a run under an account with no
   * platform-org owner (2 of 7 in prod) was diagnosable only from a raw
   * psql session against production, unaudited, which is exactly the state
   * `routes/admin.ts` exists to end.
   *
   * ITS OWN SCOPE, not a widening of `admin:read:platform`. A run transcript is
   * the single most disclosing thing on this platform — the agent's reasoning,
   * the customer's code, their commands and their test output — so it is
   * separable from the rollups and registries a radar session reads. It is a
   * READ: nothing on the `/v1/admin/fleet/*` routes writes to a tenant.
   *
   * The `admin:` prefix is what keeps it out of every role ceiling
   * (`OWNER_SCOPES` filters the prefix), out of `mintableScopes`, and reachable
   * only through the `platformAdmin` staff grant below. That carve-out is by
   * PREFIX, so a future platform scope spelled without one would land in every
   * owner's ceiling silently — `auth.test.ts`'s
   * `ADMIN_READ_SCOPES x OWNER_SCOPES` pins the disjointness as a property so
   * that lands red instead.
   */
  ReadAdminRuns: 'admin:read:runs',
  /**
   * Issue / revoke an ENTITLEMENT GRANT (epic #1259) — free capacity handed to an
   * org or a person on the platform's own margin. The one platform-admin WRITE in
   * this enum, and it is here for a mechanical reason: grants are the first admin
   * write served over REST (`POST|DELETE /v1/admin/grants*`), and `requireScope`
   * takes a `Scope`. A bespoke second gate beside it would be a second scope-check
   * mechanism to get wrong.
   *
   * Membership grants nobody anything. Every derivation above carves `admin:` out
   * structurally — `VIEWER_SCOPES` is the `read:` sweep plus the named
   * `write:self:` scopes, `MEMBER`/`ADMIN`/`OWNER_SCOPES`
   * filter the prefix, and the platform-staff grant adds only
   * `ADMIN_READ_SCOPES` and `STAFF_SESSION_WRITE_SCOPES` (`admin:bugs`) — so no
   * session, role or self-minted PAT can hold it;
   * `mintableScopes` strips `admin:*` from what a caller may mint by default, and
   * an explicit request must still be a subset of scopes the caller already has.
   * The only holder is a token an operator deliberately provisioned, exactly like
   * `admin:accounts` / `admin:users` (which stay out of this enum because their
   * verbs are MCP-only).
   *
   * The matching READ is `admin:read:orgs`: a staff radar session may see who
   * holds free capacity without being able to hand any out — the same split the
   * OSS-verified queue takes between its read and its verdict.
   */
  WriteAdminGrants: 'admin:grants',
  /**
   * Rename a goal on the platform's own roadmap (`PATCH /v1/admin/goals/:id`) —
   * a write THROUGH to GitHub, retitling the milestone or the `type/epic` issue
   * itself. Here for the same mechanical reason as `admin:grants` above: it is
   * served over REST, and `requireScope` takes a `Scope`.
   *
   * Its own scope rather than widening `admin:read:platform`, so a staff radar
   * session can watch the roadmap without being able to retitle it — the split
   * `admin:grants` takes against `admin:read:orgs`, and the reason the read is
   * safe to hand out freely. Membership grants it to nobody: every role ceiling
   * carves `admin:` out structurally, so the only holder is a token an operator
   * deliberately provisioned.
   */
  WriteAdminGoals: 'admin:goals',
  /**
   * Clear the yank on a `dz-runner` release (#1286/#1418) — the ONE door that
   * re-admits a halted build to the installable read. Here for the mechanical
   * reason `admin:grants` is: it is now served over REST
   * (`POST /v1/runners/releases/:channel/:version/unyank`) as well as MCP
   * (`box unyank`), and `requireScope` takes a `Scope`.
   *
   * It must be the SAME STRING as `@developerz/mcp`'s `SCOPE_ADMIN_RELEASES` —
   * one capability, one gate, both doors — which `contract.test.ts` proves by
   * comparing the catalogued REST scope against the MCP action scope for every
   * capability both surfaces serve.
   *
   * Why an `admin:` scope and not `write:runners`, which every other fleet write
   * rides: `runner_releases` is PLATFORM-GLOBAL (no `account_id`, no FK), so
   * un-yanking re-opens a build for every tenant's boxes, not the caller's. It
   * rode `write:runners` once, and that scope sits in the ADMIN ceiling — so any
   * admin or owner of ANY account could re-admit a build we halted for bricking
   * machines. The `admin:` prefix is what keeps it out of every ceiling above,
   * out of `SESSION_SCOPES` and out of `mintableScopes`: the only holder is a
   * token an operator deliberately provisioned.
   */
  WriteAdminReleases: 'admin:releases',
  /**
   * APPROVE / SUSPEND A USER, and manage invitations — the MCP `user` verb's
   * gate (`packages/mcp` `user.ts`). Approval is the platform's offboarding
   * lever: clearing `users.approved_at` caps that human's every session at
   * `read:account` on the next request, so this scope is the one that can lock
   * anybody out of the platform, or let anybody in.
   *
   * A `Scope` MEMBER since #2055, and it was not one before. The string was
   * MCP-only, so `POST /v1/tokens` rejected the NAME at the Zod edge with a 400
   * before any authorization question was asked — and a refusal that is a parse
   * error is not a refusal anyone can reason about. It now refuses as a 403
   * naming the scope, which is the same answer for the same request and a
   * different fact about the system.
   *
   * Making it expressible grants nobody anything. It is `admin:`-prefixed, so
   * every ceiling filters it (`OWNER_SCOPES`), `sessionScopes` never adds it,
   * `mintableScopes` strips it, and `ADMIN_WRITE_SCOPES`
   * (`apps/api/src/middleware/scopes.ts`) — the ONE list
   * the issuance door, `GET /v1/me` and the property tests all read — is what
   * makes those claims checkable rather than remembered.
   */
  WriteAdminUsers: 'admin:users',
  /**
   * ORG (ACCOUNT) MANAGEMENT WRITES — tier, seats, spend cap, and the OSS
   * verdict on the verification queue. The MCP `account` verb's operator gate.
   *
   * A `Scope` member since #2055 for the reason {@link WriteAdminUsers} is, and
   * fenced by the same four mechanisms. Its matching READ is
   * {@link ReadAdminOrgs}: a staff radar session sees every org's tier and spend
   * cap without being able to move one — the split every `admin:` write here
   * takes against its read, and the reason the reads are safe on a cookie.
   */
  WriteAdminAccounts: 'admin:accounts',
  /**
   * STAFF BUG REPORTS (plan 101 slice 05) — the MCP `bug` verb's report / list /
   * verdict over `platform_bugs`, a PLATFORM-GLOBAL table with no account fence.
   * A holder reads every staff report on the platform, which is why no tenant
   * may ever hold it.
   *
   * The one `admin:*` write a COOKIE carries: `apps/api`'s
   * `STAFF_SESSION_WRITE_SCOPES` puts it on a live, approved, consented staff
   * session, and it is disjoint from `ADMIN_WRITE_SCOPES` (the operator-only
   * writes that never ride a cookie). It writes to no tenant: it files and rules
   * on reports in our own table, and only from the platform account. It is NOT
   * free of tenant text: bodies paste tenant logs, so a stolen staff cookie can
   * read cross-tenant text via `bug list` — the exposure class the
   * `admin:read:*` radar already carries on that cookie. Cross-site use is closed
   * by `apps/api`'s `cookieWriteGuard` (every MCP call is a `POST`).
   *
   * The `admin:` prefix is the fence: `OWNER_SCOPES` filters it, so no role
   * ceiling holds it; `mintableScopes` strips it, so no self-serve PAT or OAuth
   * grant carries it. `apps/api/src/middleware/scopes.test.ts` pins both.
   */
  WriteAdminBugs: 'admin:bugs',
  // ── BYOVM gateway capability (use:*) — issue #786 ────────────────────────────
  // A single runtime capability the credential gateway checks: "may this caller
  // POST to the account's connected Slack via the platform gateway verb". The
  // `use:` prefix is deliberately its OWN class: granted ONLY on a platform-minted
  // box connection token (the BYOVM enroll path mints an `api_token` scoped to
  // exactly this — `packages/db` `createApiToken`), NEVER via a session or a role
  // ceiling. `OWNER_SCOPES` carves `use:*` out (mirroring
  // the `admin:*` carve-out), so no dashboard session, owner, or self-minted PAT
  // can silently hold it — the only holder is a box, and the gateway resolves the
  // Slack credential server-side per call. Supersedes the raw-cred `.maintainer.yml`
  // lane fenced in the same issue.
  UseIntegrationSlack: 'use:integration:slack',
} as const;
export type Scope = (typeof Scope)[keyof typeof Scope];

// ─── role → scope ceiling ────────────────────────────────────────────────────
//
// A membership `role` caps what a user-bound credential may do in its account.
// Cumulative up the ladder (owner ⊇ admin ⊇ member ⊇ viewer). Applied at BOTH
// credential doors: session resolution (`sessionScopes`, apps/api) and PAT
// resolution (`resolveApiToken`, packages/db, which returns
// `row.scopes ∩ scopeCeilingForRole(membershipRole)`), so a demotion takes
// effect on the next request for every credential the human holds.

/**
 * Scopes NO credential reaches without being an account OWNER (#1418). Carved out
 * of the viewer's `read:*` sweep and out of the admin ceiling — so the fence is a
 * property of the SCOPE, not of the role table alone.
 *
 * Both faces of the credential vault sit here, and both for the same reason.
 * **Read:** the vault's NAMES are the disclosure — a viewer on a shared account
 * learns that a repo carries `STRIPE_LIVE_KEY` in `prod`. Values are never
 * returned by any route, which is exactly why the metadata is the whole surface
 * to gate. **Write:** a `PUT` rotates the ciphertext a running box will unwrap —
 * the trust class of `write:repos` (owner), not of paying an invoice
 * (`write:billing`, admin).
 */
export const OWNER_ONLY_SCOPES: ReadonlySet<Scope> = new Set([
  Scope.ReadSecrets,
  Scope.WriteSecrets,
]);

/**
 * Viewer: every `read:*` scope except {@link OWNER_ONLY_SCOPES}, plus the two
 * SELF writes: `write:self:notifications` (the viewer's own notification
 * settings) and `write:self:identity` (their own sign-in identities, #466). They
 * are listed by name because the sweep is a `read:` prefix filter and would
 * never pick them up. They are the only writes a viewer holds, and
 * `scopes.test.ts` pins that as a property, so a third cannot arrive unnoticed. `admin:read:*`
 * is `admin:`-prefixed, so it is not swept in.
 */
const VIEWER_SCOPES: readonly Scope[] = [
  ...Object.values(Scope).filter((s) => s.startsWith('read:') && !OWNER_ONLY_SCOPES.has(s)),
  Scope.WriteSelfNotifications,
  Scope.WriteSelfIdentity,
];
/**
 * Member: the working contributor — viewer + the contributor writes (tasks, KB,
 * artifacts, dashboards), each gated at its route for per-item/org ownership.
 */
const MEMBER_SCOPES: readonly Scope[] = [
  ...VIEWER_SCOPES,
  Scope.WriteTasks,
  Scope.WriteKb,
  Scope.WriteArtifacts,
  Scope.WriteDashboards,
  // A member manages their OWN inference (personal BYOK key + model default,
  // epic #635) — their own work, not the org pool (that stays `write:billing`).
  Scope.WriteSelfKeys,
  // …and their OWN system-prompt layer (epic #635 cascade); the org + repo
  // layers stay owner/admin (`write:prompts`).
  Scope.WriteSelfPrompt,
];
/**
 * Admin: runs the fleet + billing on top of member, plus the account-config
 * writes of the same trust class as billing — integrations, donation earmarks,
 * member management, the org/repo prompt layers. The credential vault is NOT
 * here (#1418): {@link OWNER_ONLY_SCOPES}. `write:members` is further capped per
 * target by `canManageMember` — an admin manages member/viewer, never owner/admin.
 */
const ADMIN_SCOPES: readonly Scope[] = [
  ...MEMBER_SCOPES,
  Scope.WriteRunners,
  // #7011: hold a repo's merges for review (owner ruling 2026-10-21) — the Merge Gate.
  Scope.WriteMergeGate,
  // #7010: name squads of repos (owner ruling 2026-10-21) — Fleet Live's grouping.
  Scope.WriteSquads,
  Scope.WriteBilling,
  Scope.WriteIntegrations,
  Scope.WriteDonations,
  Scope.WriteMembers,
  Scope.WritePrompts,
];
/**
 * Owner: every ACCOUNT-scoped scope. Excludes `admin:*` (operating the platform
 * is not owning an account — only the staff grant or an operator token carries
 * those) and `use:*` (the box-gateway capability, #786: an owner is not a box).
 * This is the ONE set that keeps {@link OWNER_ONLY_SCOPES}.
 */
const OWNER_SCOPES: readonly Scope[] = Object.values(Scope).filter(
  (s) => !s.startsWith('admin:') && !s.startsWith('use:'),
);

/** Role → the maximum scope set a credential bound to it may hold in its account. */
export const ROLE_SCOPE_CEILING: Readonly<Record<Role, ReadonlySet<Scope>>> = {
  [Role.Owner]: new Set(OWNER_SCOPES),
  [Role.Admin]: new Set(ADMIN_SCOPES),
  [Role.Member]: new Set(MEMBER_SCOPES),
  [Role.Viewer]: new Set(VIEWER_SCOPES),
};

/** The scope ceiling for a membership `role` — the ONE lookup both credential doors use. */
export function scopeCeilingForRole(role: Role): ReadonlySet<Scope> {
  return ROLE_SCOPE_CEILING[role];
}

// ─── who can mint a credential ───────────────────────────────────────────────

/**
 * THE PAT SCOPE CEILING, ONCE — "a PAT never carries a scope its minter does not
 * hold", plus the two classes no PAT may carry at all.
 *
 * It lived as three copies (plan 101, finding 13-8): `unmintableRefusal` in
 * `apps/api/src/routes/tokens.ts`, a hand-restated twin inside
 * `packages/mcp/src/army/token.ts`'s `mint` action, and `mintableScopes` feeding
 * both. Parity between the two doors held — `tokens.test.ts` drives both over
 * one input — but the CONTROL that decides what a credential is could not see
 * what its minter held, so a third caller of `TokenControl.mint` would have
 * minted an escalated PAT with nothing to stop it. A comment on the MCP copy had
 * already asserted lockstep while the two filtered different prefixes.
 *
 * It is pure and it lives HERE, beside {@link ROLE_SCOPE_CEILING}, so that
 * {@link mintingRoles} can apply it to the ceiling instead of restating it
 * (#4624). Both doors import it from here: `@developerz/mcp`'s
 * `resolveMintScopes` (`army/token.ts`) enforces it on every mint, and
 * `apps/api`'s `mintableScopes` is a typed wrapper over it, so `/v1/me`'s
 * published `mintable_scopes` and the OAuth consent screen can never offer a
 * scope the mint refuses.
 *
 * TWO CARVE-OUTS, and the reason each is structural rather than a preference.
 * `admin:*` is platform-operator reach handed out by the staff grant and `use:*`
 * is the BYOVM gateway capability minted onto a box connection token. Both are
 * recomputed per request on a session, while a PAT carries its stamped row: the
 * resolve re-caps a human PAT to its live ROLE ceiling (`resolveApiToken`, rung 4),
 * which no role carries either class in, but a user-less row is read verbatim —
 * so freezing either onto a row could outlive the grant that allowed it.
 *
 * THE MINT GATE — a caller may mint when it holds `write:tasks` OR
 * `write:account`, and nothing else opens the door.
 *
 * `write:tasks` is the DISPATCH scope: the working authority a member already
 * has over their own work. `write:account` is the account's ADMINISTRATION
 * scope, and it stays the owner's alone. Minting needs EITHER because minting
 * is not administration: a member mints a credential for work they are already
 * allowed to dispatch, and the credential they mint can never carry a scope
 * they do not hold themselves. The owner keeps the door they had, so nothing
 * that worked before stops working.
 *
 * Empty without either: a caller who can neither dispatch nor administer the
 * account cannot mint a credential for it, whatever else they hold. That is
 * exactly the viewer's position — it holds neither — and it is the whole
 * difference between a viewer and a member.
 */
export function mintableScopeSet(held: ReadonlySet<string>): string[] {
  if (!held.has(Scope.WriteTasks) && !held.has(Scope.WriteAccount)) return [];
  return Array.from(held).filter((s) => !s.startsWith('admin:') && !s.startsWith('use:'));
}

/**
 * The sentence a mint refusal for a user-less caller carries — the ONE spelling,
 * shared by both doors through {@link mintVocabulary}'s consumers.
 */
export const MINT_NEEDS_HUMAN =
  'this credential has no person behind it, and a token it minted would keep its scopes forever, never re-capped to a live role: mint from a signed-in session or a user-bound token';

/**
 * What THIS caller may mint — the one decision both mint doors (`POST /v1/tokens`,
 * MCP `token mint`, via `resolveMintScopes`) and every published `mintable_scopes`
 * read (#7426 review).
 *
 * A mint needs a HUMAN. `resolveApiToken` re-caps a user-bound PAT to its human's
 * live role on every request and reads a user-less row (a box connection token, a
 * legacy PAT) verbatim, so a PAT minted by a user-less caller would be a
 * permanent credential no offboarding narrows. 7 live user-less tokens held
 * `write:tasks` on 2026-10-27, which since #7426 is the mint gate. So the absent
 * human is its own refusal, `no_human`, never an empty list a caller could read
 * as "your role holds nothing mintable".
 */
export type MintVocabulary =
  | { readonly kind: 'mintable'; readonly scopes: string[] }
  | { readonly kind: 'no_human' };

export function mintVocabulary(caller: {
  readonly scopes: ReadonlySet<string>;
  readonly userId?: string | null | undefined;
}): MintVocabulary {
  if (!caller.userId) return { kind: 'no_human' };
  return { kind: 'mintable', scopes: mintableScopeSet(caller.scopes) };
}

/**
 * The account roles whose ceiling can mint a credential at all. Highest role
 * first.
 *
 * Derived, never listed: it is {@link mintableScopeSet} applied to
 * {@link ROLE_SCOPE_CEILING}, the same two facts `POST /v1/tokens` and the OAuth
 * consent apply to a live caller. Six refusals once hand-typed "an owner or
 * admin" while every ceiling but the owner's minted nothing (#4624); a role
 * table that changes now changes every sentence that names a minter.
 *
 * OWNER, ADMIN AND MEMBER today — the three ceilings that hold `write:tasks`,
 * the dispatch scope the mint opens on. The viewer holds neither it nor
 * `write:account`, so it is out.
 */
export function mintingRoles(): Role[] {
  return Object.values(Role).filter(
    (role) => mintableScopeSet(ROLE_SCOPE_CEILING[role]).length > 0,
  );
}

// ─── whose tokens a caller may list, rename and revoke ────────────────────────

/**
 * WHICH `api_tokens` rows a roster read or a rename/revoke may touch (#7426) —
 * the fence the db queries take. Discriminated and REQUIRED at every query, never
 * an optional `userId` whose absence reads as "the whole account": a forgotten
 * argument must not fail open.
 *
 * - `account` — every row of the account: a holder of `write:account` (an owner,
 *   or a credential minted with that scope), exactly the reach these doors had
 *   before members could mint.
 * - `own` — only rows whose `user_id` is this human. Since #7426 a member or an
 *   admin mints their own PATs (and their MCP connectors' grants), so they must be
 *   able to see and kill those — and nobody else's.
 */
export type TokenFence =
  | { readonly scope: 'account' }
  | { readonly scope: 'own'; readonly userId: string };

/** {@link TokenFence}, or the third answer: this caller reaches no row at all. */
export type TokenReach = TokenFence | { readonly scope: 'none' };

/**
 * The ONE decision of a caller's token reach, for the roster read (`read`) or for
 * a rename/revoke (`manage`). `write:account` reaches the account. Otherwise a
 * HUMAN reaches their own rows, given the act's scope — `read:account` to list,
 * `write:tasks` (the mint gate, {@link mintableScopeSet}) to rename or revoke. A
 * user-less credential without `write:account` owns no row, so it reaches none.
 *
 * Property the roster's `can_manage` rests on: when `manage` is not `none`, every
 * row `read` reaches is one `manage` reaches too (both are `account`, or both are
 * the same human's `own`), so "may this caller manage the rows it was shown" is
 * one boolean for the whole page.
 */
export function tokenReach(
  caller: { readonly scopes: ReadonlySet<string>; readonly userId?: string | null },
  act: 'read' | 'manage',
): TokenReach {
  if (caller.scopes.has(Scope.WriteAccount)) return { scope: 'account' };
  const gate = act === 'read' ? Scope.ReadAccount : Scope.WriteTasks;
  if (!caller.scopes.has(gate) || !caller.userId) return { scope: 'none' };
  return { scope: 'own', userId: caller.userId };
}

const ROLE_WITH_ARTICLE: Readonly<Record<Role, string>> = {
  [Role.Owner]: 'an owner',
  [Role.Admin]: 'an admin',
  [Role.Member]: 'a member',
  [Role.Viewer]: 'a viewer',
};

/**
 * {@link mintingRoles} as the actor of a sentence — "an owner, an admin, or a
 * member" today. The ONE spelling of "who can mint for you". Four runtime sites
 * interpolate it: `missingScopeMessage` (`packages/mcp/src/registry.ts`),
 * `scopeRefusedFooter` (`packages/mcp/src/help-refused-footer.ts`),
 * `SCOPE_CEILING_REMEDY` (`packages/mcp/src/tools/rung-verdicts.ts`) and
 * `roleRefusal` (`apps/api/src/oauth/authorize-refusal.ts`). So do two dashboard
 * fixtures copying the remedy (`apps/dashboard/src/lib/repo-readiness-mocks.ts`,
 * `apps/dashboard/src/routes/RepoDetail.fixtures.test-support.ts`).
 *
 * NOT the `plan_selected` rung (`packages/mcp/src/tools.ts`): that one asks who
 * may ADMINISTER the account, and administration is `write:account` — the owner
 * alone. It interpolates {@link writeAccountHoldersPhrase} instead, because
 * "who can mint" and "who may administer" stopped being one role set the day
 * the mint opened on `write:tasks`.
 *
 * `scripts/mint-authority.guard.test.ts` fails a hand-typed role pair beside a
 * mint, or a role that cannot mint named as its actor, in what it reads: TS/TSX
 * string and template literals, md/mdx sentences and JSON lines under `apps/`,
 * `packages/` and `docs/`, in English only. Its header lists what it cannot see.
 *
 * An empty {@link mintingRoles} is not handled here: `scopes.test.ts` pins it to
 * the three minting roles, so a ceiling that leaves nobody able to mint fails
 * there first.
 */
export function mintersPhrase(): string {
  return rolesPhrase(mintingRoles());
}

/**
 * The roles whose ceiling carries `scope`, as the actor of a sentence — "an
 * owner" for `write:account`. Since #7426 "who can mint" ({@link mintersPhrase})
 * and "who holds `write:account`" are different role sets, so a sentence about an
 * owner-only write names its holders through this, never through the minters.
 */
export function scopeHoldersPhrase(scope: Scope): string {
  return rolesPhrase(Object.values(Role).filter((r) => ROLE_SCOPE_CEILING[r].has(scope)));
}

function rolesPhrase(roles: readonly Role[]): string {
  return new Intl.ListFormat('en', { type: 'disjunction' }).format(
    roles.map((r) => ROLE_WITH_ARTICLE[r]),
  );
}

/**
 * The account roles whose ceiling holds `write:account` — the ADMINISTRATORS,
 * as distinct from the minters.
 *
 * A separate set because the two stopped being one set. The mint opens on
 * `write:tasks` OR `write:account`, so a member may mint while never holding
 * `write:account`; a sentence about who may ADMINISTER the account must not
 * name the minters, or it sends a member to perform a grant they cannot make.
 */
export function writeAccountHolderRoles(): Role[] {
  return Object.values(Role).filter((role) => ROLE_SCOPE_CEILING[role].has(Scope.WriteAccount));
}

/**
 * {@link writeAccountHolderRoles} as the actor of a sentence — "an owner"
 * today. The ONE spelling of "who may administer this account", for the
 * sentences that are about administration rather than about minting.
 */
export function writeAccountHoldersPhrase(): string {
  return new Intl.ListFormat('en', { type: 'disjunction' }).format(
    writeAccountHolderRoles().map((r) => ROLE_WITH_ARTICLE[r]),
  );
}
